import { Chip, Tooltip } from "@mui/material";

import { reasonLabel, type LocalVerdict } from "./verdict.js";

/**
 * The answer for one document and one account, in one glance.
 *
 * The reason matters as much as the answer. "Hidden" alone sends staff
 * hunting through three layers; "Product not purchased" versus "Blocked —
 * folder" versus "Not on the whitelist" each point straight at the thing to
 * change. So the pill always carries WHY, and the colour only carries the
 * yes/no.
 *
 * Three colours, not eleven: green for visible, red for a deliberate block,
 * plain grey for "not reached". A red pill means a human decided this; grey
 * means nobody did and the rules simply did not include it. Staff read that
 * distinction constantly when auditing an account, and it is invisible if
 * every hidden row looks the same.
 */
export function VerdictPill({ verdict }: { verdict: LocalVerdict }): JSX.Element {
  const label = reasonLabel(verdict);
  // A reseller hiding a document from its customer is a human decision too.
  const deliberate = verdict.reason === "rule_deny" || verdict.reason === "reseller_deny";

  return (
    <Tooltip title={label} placement="left">
      <Chip
        size="small"
        label={label}
        data-testid="verdict-pill"
        data-visible={verdict.visible ? "true" : "false"}
        data-reason={verdict.reason}
        color={verdict.visible ? "success" : deliberate ? "error" : "default"}
        variant={verdict.visible ? "filled" : "outlined"}
        sx={{
          height: 22,
          fontSize: 12,
          maxWidth: 220,
          "& .MuiChip-label": { px: 1, overflow: "hidden", textOverflow: "ellipsis" },
        }}
      />
    </Tooltip>
  );
}
